/**
 * Quando le informazioni economiche dell'itinerario sono consolidate.
 *
 * Il discrimine e' il denaro impegnato, non l'invio del preventivo: finche' il cliente non
 * ha avviato nessun pagamento il TD lavora libero — totale, markup, attivita' e modalita' di
 * pagamento si cambiano quanto serve. Dal primo pagamento in poi quello che e' gia'
 * consolidato non si tocca piu': totale e costi che ci concorrono. Restano modificabili le
 * rate e tutto il contenuto non economico (descrizioni, media, documenti, orari, note).
 *
 * Lo status dell'itinerario non serve a dedurlo, anzi inganna: con un piano rate
 * mark_installment_paid porta l'itinerario a 'completed' gia' al primo acconto.
 *
 * Il blocco vero e' nel database: guard_locked_itinerary_pricing_trigger e
 * guard_locked_itinerary_activities_trigger, entrambi su itinerary_economics_locked(). Qui
 * si decide solo cosa disabilitare, per non far arrivare l'utente a un errore.
 *
 * ⚠️ La rata ancora aperta NON e' protetta. Questo commento citava un
 * `assert_open_installment_unchanged` che non esiste — mai stato nel repo ne' su PROD
 * (verificato 2026-08-25). L'unico guard sulle rate e' guard_paid_installment, che copre
 * quelle gia' incassate: ribilanciando le rate senza toccare il totale si puo' cambiare
 * l'importo della rata per cui il cliente ha gia' un IBAN. Vedi guide/v2/10_PAYMENTS.md,
 * "Buchi noti".
 */

import { ItineraryStatus } from '../models/itinerary.model';
import { IPaymentPlan } from '../models/payment-plan.model';
import { hasPaymentInFlight } from './payment-plan.utils';

/**
 * Le informazioni economiche dell'itinerario sono congelate.
 *
 * `payment_processing` conta quanto una rata incassata: mentre un bonifico e' in viaggio
 * l'importo e' gia' stato comunicato al cliente insieme all'IBAN, e cambiarlo adesso vorrebbe
 * dire fargli versare una cifra che non corrisponde piu' a nulla. Il TA aspetta l'esito, poi
 * opera.
 *
 * Rispecchia `itinerary_economics_locked()` lato database. Attenzione a non confonderla con
 * `itinerary_has_payments()`, che ha una soglia piu' alta (solo l'incasso confermato) e vale
 * per il ritiro del preventivo: quello resta possibile con un tentativo aperto, ed e' anzi la
 * via d'uscita da un bonifico che non arriva mai — il ritiro lo porta a 'expired'.
 */
export const areEconomicsLocked = (plan: IPaymentPlan | null | undefined): boolean =>
    hasCollectedMoney(plan) || hasPaymentInFlight(plan);

/**
 * Il viaggiatore ha davvero incassato: soglia del **ritiro del preventivo**, piu' alta di
 * quella del lock sui prezzi.
 *
 * La differenza e' tutta in `payment_processing`, ed e' voluta. Se contasse anche qui, un
 * cliente che apre il checkout sul bonifico e non conclude toglierebbe al TA la possibilita'
 * di ritirare il preventivo — che e' esattamente la via d'uscita da quella situazione: il
 * ritiro chiude i tentativi aperti (`revoke_itinerary_quote` li porta a 'expired') e sblocca
 * anche i prezzi. Rispecchia `itinerary_has_payments()` lato database.
 */
export const hasCollectedMoney = (plan: IPaymentPlan | null | undefined): boolean =>
    Boolean(plan?.installments.some((inst) => inst.status === 'paid'));

/**
 * Il viaggio e' saldato: non resta nulla da incassare, e per il TD e' un viaggio chiuso.
 *
 * Lo status 'completed' da solo non lo dimostra — con un piano rate ci arriva gia' al primo
 * acconto, e un viaggio con il solo acconto versato e' tutt'altro che concluso. Quando il
 * piano c'e' comanda il piano; lo status resta il ripiego per gli itinerari senza piano,
 * dove l'incasso e' stato registrato altrove.
 *
 * Le rate annullate non contano: sono importi che il cliente non deve piu'.
 */
export const isItinerarySettled = (
    plan: IPaymentPlan | null | undefined,
    status?: ItineraryStatus,
): boolean => {
    const active = plan?.installments.filter((inst) => inst.status !== 'cancelled') ?? [];
    if (active.length > 0) return active.every((inst) => inst.status === 'paid');
    return status === 'completed';
};

/**
 * La rata attualmente aperta al pagamento: la prima ancora da saldare. Le rate si pagano in
 * ordine, quindi e' l'unica su cui il cliente possa avere un pagamento in corso — e l'unica,
 * fra quelle non incassate, che va protetta insieme a loro.
 */
export const openInstallmentId = (plan: IPaymentPlan | null | undefined): string | null =>
    plan?.installments.find((inst) => inst.status === 'pending')?.id ?? null;
